import { compact, isEmpty, sumBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   InteractiveTableValidation,
   JustifyContent,
   ValueRendererProps,
} from '@dreryk/react-components';

import { interactiveTableKeys } from '../../translations';

import { DiagnosesTableBean } from './DiagnosesInteractiveTable.types';

export const useValidation = (): InteractiveTableValidation<DiagnosesTableBean> => {
   return useCallback((diagnoses) => {
      if (isEmpty(diagnoses)) {
         return true;
      }

      const mainCount = sumBy(diagnoses, (diagnosis) => Number(diagnosis.main));
      if (mainCount < 1) {
         return interactiveTableKeys.validation.noMainDiagnosis;
      }

      if (mainCount > 1) {
         return interactiveTableKeys.validation.tooManyMainDiagnoses;
      }

      return true;
   }, []);
};

export const useColumns = <TABLE_BEAN extends DiagnosesTableBean>(
   columns?: InteractiveTableColumnConfiguration<TABLE_BEAN>[]
): InteractiveTableColumnConfiguration<TABLE_BEAN>[] => {
   return useMemo(() => {
      const dynamicColumns = columns ?? [];
      const columnWidths = createDynamicColumnsWidth<'main' | 'name' | 'code' | 'options' | string>({
         code: '76px',
         name: '100%',
         main: '70px',
         options: '40px',
         ...dynamicColumns.reduce<Record<string, string>>((result, column) => {
            result[column.name] = column.width;
            return result;
         }, {}),
      });

      return compact([
         {
            name: 'code',
            label: interactiveTableKeys.headers.icd10,
            width: columnWidths.code,

            renderer: ({ bean, index }) => {
               return (
                  <InteractiveTable.TextViewCell testId={'code'} name={'code'} index={index} defaultValue={bean.code} />
               );
            },
         },
         {
            name: 'name',

            renderer: ({ bean, index }) => {
               return (
                  <InteractiveTable.TextViewCell testId={'name'} name={'name'} index={index} defaultValue={bean.name} />
               );
            },
            label: interactiveTableKeys.headers.name,
            width: columnWidths.name,
         },
         {
            name: 'main',
            label: interactiveTableKeys.headers.main,
            width: columnWidths.main,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,

            renderer: ({ bean, index }): JSX.Element | null => {
               return (
                  <InteractiveTable.RadioCell
                     testId={'main'}
                     name={'main'}
                     index={index}
                     defaultValue={bean.main}
                     validationRules={{ required: true }}
                  />
               );
            },
         },
         ...dynamicColumns.map((column) => ({
            ...column,
            width: columnWidths[column.name],
         })),
         {
            name: 'options',
            width: columnWidths.options,

            renderer: ({ index }: ValueRendererProps<TABLE_BEAN>) => {
               return <InteractiveTable.DeleteRowCell index={index} />;
            },
         },
      ]);
   }, [columns]);
};
